class AbandonmentPopup extends HTMLElement {
  constructor() {
    super();
    this.disabled = this.dataset.disabled ?? false;

    this.getContentRoot = () =>
      this.querySelector('.abandonment-popup__grid, .abandonment-popup__container');

    this.closeBtn = this.querySelector('.abandonment-popup__close');
    this.applyDiscountEl = this.querySelector(".js-apply-discount");

    if (this.applyDiscountEl) {
      this.handleApplyDiscount();
    }

    this._escHandler = (e) => { if (e.key === 'Escape') this.close(); };
    this._overlayClick = (e) => {
      const content = this.getContentRoot();
      if (!content) return;
      if (!content.contains(e.target)) this.close();
    };

    this.inactivityMs = parseInt(this.dataset.inactivityMs || '300000', 10);
    this._inactivityTimer = null;
    this._lastCartCount = null; 
    this._cartFetchTs   = 0;

    this._onCloseClick = (e) => { e.preventDefault(); this.close(); };

    this._onMouseOutTop = (e) => {
      if (this.isBlocked()) return;
      if (!e.relatedTarget && e.clientY <= 0) this.open();
    };

    this._onVisibilityChange = () => {
      if (this.isBlocked()) return;
      if (document.hidden) this.open();
    };

    this._onWindowBlur = () => {
      if (this.isBlocked()) return;
      this.open();
    };

    this._onPopState = () => {
      if (this.isBlocked()) return;
      this.open();
      history.pushState({ lsgAbandonmentPopup: 1 }, '');
    };

    this._onPageHide = () => {
      if (this.isBlocked()) return;
      this.open();
    };

    this._edgeSwipeAt = 0;
    this._onTouchStartEdge = (e) => {
      if (this.dataset.edgeSwipe !== '1') return;
      const t = e.touches && e.touches[0];
      if (!t) return;
      if (t.clientX < 16) this._edgeSwipeAt = Date.now();
    };
    this._onTouchEndEdge = () => {
      if (this.dataset.edgeSwipe !== '1') return;
      if (this._edgeSwipeAt && Date.now() - this._edgeSwipeAt < 800) this.open();
      this._edgeSwipeAt = 0;
    };

    this._onUserActivity = () => this._resetInactivityTimer();
  }

  connectedCallback() {
    if (this.disabled) return;

    this.closeBtn?.addEventListener('click', this._onCloseClick);
    this.addEventListener('click', this._overlayClick);
    document.addEventListener('keydown', this._escHandler);

    document.addEventListener('mouseout', this._onMouseOutTop);
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    window.addEventListener('blur', this._onWindowBlur);

    if (!history.state || !history.state.lsgAbandonmentPopup) {
      history.pushState({ lsgAbandonmentPopup: 1 }, '');
    }
    window.addEventListener('popstate', this._onPopState);

    window.addEventListener('pagehide', this._onPageHide);
    document.addEventListener('touchstart', this._onTouchStartEdge, { passive: true });
    document.addEventListener('touchend', this._onTouchEndEdge, { passive: true });

    ['mousemove','keydown','scroll','touchstart','click'].forEach(evt =>
      document.addEventListener(evt, this._onUserActivity, { passive: true })
    );

    this._resetInactivityTimer();
  }

  get cookieName() {
    return this.dataset.cookieKey || 'lsg_abandonment_popup_blocked';
  }

  disconnectedCallback() {
    this.closeBtn?.removeEventListener('click', this._onCloseClick);
    this.removeEventListener('click', this._overlayClick);
    document.removeEventListener('keydown', this._escHandler);

    document.removeEventListener('mouseout', this._onMouseOutTop);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('blur', this._onWindowBlur);
    window.removeEventListener('popstate', this._onPopState);

    window.removeEventListener('pagehide', this._onPageHide);
    document.removeEventListener('touchstart', this._onTouchStartEdge);
    document.removeEventListener('touchend', this._onTouchEndEdge);

    ['mousemove','keydown','scroll','touchstart','click'].forEach(evt =>
      document.removeEventListener(evt, this._onUserActivity)
    );
    this._clearInactivityTimer();
  }

  handleApplyDiscount() {
    const el = this.applyDiscountEl;
    if (!el) return;

    el.addEventListener("click", (e) => {
      e.preventDefault();
      const code = (el.dataset.discountCode || "").trim();
      const current = this.applyDiscountEl.dataset.url;
      let url = current;
      if (code) {
        url = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(current)}`;
      }
      this.setBlockCookie();
      location.assign(url);
    });
  }

  async _fetchCartCount() {
    const now = Date.now();
    if (now - this._cartFetchTs < 1000 && this._lastCartCount != null) {
      return this._lastCartCount;
    }
    this._cartFetchTs = now;

    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('cart.js fetch failed');
      const cart = await res.json();
      this._lastCartCount = Number(cart?.item_count || 0);
    } catch {
      this._lastCartCount = 0;
    }
    return this._lastCartCount;
  }

  async open() {
    if (this.isBlocked()) return;

    const cartItemsCount = await this._fetchCartCount();

    if (cartItemsCount < 2) return;

    if (!this.classList.contains('hidden')) return;
    this.classList.remove('hidden');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
  }

  close() {
    this.setBlockCookie();
    this.classList.add('hidden');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
  }

  isBlocked() {
    return document.cookie.split('; ').some(c => c.startsWith(this.cookieName + '='));
  }

  setBlockCookie() {
    document.cookie = `${this.cookieName}=1; Max-Age=259200; Path=/`;
  }

  _resetInactivityTimer() {
    if (this.isBlocked()) return;
    this._clearInactivityTimer();
    this._inactivityTimer = setTimeout(() => this.open(), this.inactivityMs);
  }

  _clearInactivityTimer() {
    if (this._inactivityTimer) {
      clearTimeout(this._inactivityTimer);
      this._inactivityTimer = null;
    }
  }
}

customElements.define('lsg-abandonment-popup', AbandonmentPopup);
